import { useMemo } from 'react';
import styled from 'styled-components';

import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { ClientPortalMapping, normalizePortalString } from 'services/api/portalMappings';

import {
  CollectedFacility,
  collectedFacilityLabel,
  FacilityOption,
  mappedPortalFacility,
  pickedFacility,
  portalFacility,
  VALUE_NAMES,
} from '../constants';
import FacilityMappingReview, {
  ExternalMappingRow,
  FacilityPicks,
  MappingColumnLabels,
} from '../FacilityMappingReview';

import { MAPPED_FACILITIES_VALUE } from './fieldResolver';
import { FieldRow, ReadOnlyValue } from './TaskSection';

/**
 * The Facility section, which is not one control (OLIO-3022).
 *
 * Which rendering appears is decided by what the run is actually waiting on, never by the
 * retrieval mode: a script-captured facility whose string the client has already mapped resolves
 * silently and never asks anybody anything, so "captured by a script" does not imply "unmapped".
 *
 *   1. the mapping step is waiting     -> the mapping review, lifted whole from the step view
 *   2. retrieval is waiting            -> a plain pick from this user's own facilities
 *   3. neither                         -> what the facilities resolved to, read-only
 *
 * Several facilities are supported in all three, QUIETLY: the controls handle more than one, and
 * nothing says so. No `+ Add facility`, no plural labels, no empty second row — a portal that
 * never names two never teaches anyone it could.
 */

interface Props {
  workflow: Workflow;
  label: string;
  present: boolean;
  mapping?: WorkflowAction;
  retrieval?: WorkflowAction;
  picks: FacilityPicks;
  onPicksChange: (picks: FacilityPicks) => void;
  // The picks made when RETRIEVAL owns the section — this user's own facilities, already
  // resolved, so nothing needs mapping afterwards.
  entered: (FacilityOption | null)[];
  onEnteredChange: (picks: (FacilityOption | null)[]) => void;
  // The pre-mapping review's inputs, derived by the slideout (which also builds the submit
  // payload from them): the strings the retrieval staged, and the pick each one starts with.
  stagedStrings: string[];
  autoMatch: Record<string, FacilityOption | undefined>;
}

export default function FacilitySection({
  workflow,
  label,
  present,
  mapping,
  retrieval,
  picks,
  onPicksChange,
  entered,
  onEnteredChange,
  stagedStrings,
  autoMatch,
}: Props) {
  const { facilities } = useAllowedFacilities();

  if (mapping) {
    return (
      <div data-testid='facility-section-mapping'>
        <FacilityMappingReview action={mapping} picks={picks} onPicksChange={onPicksChange} />
      </div>
    );
  }

  if (retrieval) {
    // A capture the run already staged (a sweep's, or an earlier extension run's) is reviewed
    // as a PRE-mapping: the same two-column surface the mapping step draws, answered before
    // that step ever runs. A string the client already knows (a saved mapping, or an exact
    // name match) starts resolved; the rest start amber, exactly as the mapping step would
    // have shown them one submit later.
    if (stagedStrings.length > 0) {
      const setPick = (name: string, option: FacilityOption | null) =>
        onPicksChange({ ...picks, byExternal: { ...picks.byExternal, [name]: option } });

      return (
        <StagedRows data-testid='facility-section-staged'>
          <MappingColumnLabels />
          {stagedStrings.map((name) => (
            <ExternalMappingRow
              key={name}
              name={name}
              pick={effectiveFacilityPick(name, picks, autoMatch)}
              options={facilities.map((group) => ({ id: group.id, name: group.name }))}
              onPick={(option) => setPick(name, option)}
              onClear={() => setPick(name, null)}
            />
          ))}
        </StagedRows>
      );
    }
    return <FacilityPicker label={label} present={present} entered={entered} onEnteredChange={onEnteredChange} />;
  }

  return <MappedFacilities workflow={workflow} label={label} present={present} />;
}

// The strings the retrieval staged for `facilities`, in whatever shape the script sent them:
// a bare string, a list of strings, or CollectedFacility entries.
//
// Falls back to the creation-time default (OLIO-3076) only when the retrieval staged nothing: a
// sweep row's SNF column is a portal string like any other, so it maps and confirms through this
// same section rather than needing a surface of its own.
export function stagedFacilityStrings(retrieval?: WorkflowAction): string[] {
  if (!retrieval) return [];
  const staged = retrieval.outputFor(VALUE_NAMES.FACILITIES) ?? retrieval.defaultFor(VALUE_NAMES.FACILITIES);
  const entries = typeof staged === 'string' ? [staged] : Array.isArray(staged) ? staged : [];
  return entries
    .map((entry) =>
      typeof entry === 'string' ? entry : String((entry as { externalValue?: unknown })?.externalValue ?? '')
    )
    .map((name) => name.trim())
    .filter(Boolean);
}

// The pick a string starts with, before any gesture: the saved mapping's facility when the
// client already knows this string — the same verdict the mapping step's bag would reach,
// matched by the api's own rule (normalizePortalString) — else the facility whose name equals
// it. A saved mapping pointing outside this user's facility list can't be named here, so it
// starts unmatched and the person picks from their own list.
export function facilityAutoMatches(
  strings: string[],
  mappings: ClientPortalMapping[],
  options: FacilityOption[]
): Record<string, FacilityOption | undefined> {
  const byId = new Map(options.map((option) => [option.id, option]));
  const byName = new Map(options.map((option) => [normalizePortalString(option.name), option]));
  const saved = new Map(mappings.map((mapping) => [normalizePortalString(mapping.externalValue), mapping.mappedValue]));

  return Object.fromEntries(
    strings.map((name) => {
      const key = normalizePortalString(name);
      const savedId = saved.get(key);
      return [name, (savedId && byId.get(savedId)) || byName.get(key)];
    })
  );
}

// `undefined` in picks means "no gesture yet" (the auto-match stands); `null` is an explicit
// Clear mapping. The distinction is what lets clearing beat a match that would otherwise
// reassert itself on the next render.
export function effectiveFacilityPick(
  name: string,
  picks: FacilityPicks,
  autoMatch: Record<string, FacilityOption | undefined>
): FacilityOption | null {
  const chosen = picks.byExternal[name];
  return chosen !== undefined ? chosen : (autoMatch[name] ?? null);
}

// The retrieval completion's `facilities`. Every collected string rides along; a pick pairs
// with its string — a mapping the client keeps — EXCEPT when it overrides an auto-match:
// choosing something other than what the string already means is a manual statement about
// this referral, not a remapping of the string, so the pick goes alone.
export function preMappedFacilities(
  strings: string[],
  picks: FacilityPicks,
  autoMatch: Record<string, FacilityOption | undefined>
): CollectedFacility[] {
  return strings.map((name) => {
    const pick = effectiveFacilityPick(name, picks, autoMatch);
    if (!pick) return portalFacility(name);
    const matched = autoMatch[name];
    if (matched && pick.id !== matched.id) return pickedFacility(pick.id);
    return mappedPortalFacility(name, pick.id);
  });
}

export function preMappingComplete(
  strings: string[],
  picks: FacilityPicks,
  autoMatch: Record<string, FacilityOption | undefined>
): boolean {
  return strings.every((name) => effectiveFacilityPick(name, picks, autoMatch));
}

// The entry path: the person names the facility themselves, so what this collects is an Olio
// facility and the mapping step has nothing to resolve. A second row appears only once the first
// is filled — support for several, without an affordance announcing it.
function FacilityPicker({
  label,
  present,
  entered,
  onEnteredChange,
}: Pick<Props, 'label' | 'present' | 'entered' | 'onEnteredChange'>) {
  const { facilities } = useAllowedFacilities();
  const options: FacilityOption[] = useMemo(
    () => facilities.map((group) => ({ id: group.id, name: group.name })),
    [facilities]
  );

  const rows = entered.length > 0 && entered[entered.length - 1] ? [...entered, null] : entered;

  const setRow = (index: number, option: FacilityOption | null) => {
    const next = rows.map((pick, i) => (i === index ? option : pick));
    // Trailing empties are never kept: the extra row exists to be filled, not to persist as an
    // empty control once the person changes their mind.
    while (next.length > 1 && !next[next.length - 1]) next.pop();
    onEnteredChange(next);
  };

  return (
    <div data-testid='facility-section-picker'>
      {rows.map((row, index) => (
        // Index keys: rows are positional (no identity beyond their slot).
        <FieldRow key={index} label={index === 0 ? label : ''} present={index === 0 ? present : !!row}>
          <Select<FacilityOption>
            inputId={`task-facility-${index}`}
            placeholder='Select a facility'
            options={options.filter(
              (option) => option.id === row?.id || !entered.some((pick) => pick?.id === option.id)
            )}
            value={row}
            onChange={(option) => setRow(index, option)}
            menuPortalTarget={document.body}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
          />
        </FieldRow>
      ))}
    </div>
  );
}

// Already settled. The mapped ids are bare uuids in `values[]` — the option list that named them
// lived on the mapping action's staged result and is gone once it completed — so the names are
// resolved against this user's allowed facilities, with the portal's own collected string as the
// fallback. A mapped facility can legitimately sit outside the acting user's allowed set, which
// is why an id we cannot name says so rather than reading as a facility called "facility".
function MappedFacilities({ workflow, label, present }: Pick<Props, 'workflow' | 'label' | 'present'>) {
  const { facilities } = useAllowedFacilities();
  const mappedIds = (workflow.valueFor(MAPPED_FACILITIES_VALUE)?.value ?? []) as unknown;
  const collected = (workflow.valueFor(VALUE_NAMES.FACILITIES)?.value ?? []) as unknown;
  const collectedEntries = Array.isArray(collected) ? collected : [];

  const ids = Array.isArray(mappedIds) ? mappedIds : [];
  // The collected labels are only trustworthy positionally when the two lists actually
  // correspond — they come from different steps (collection vs resolution), so on any length
  // mismatch a borrowed label could name an id with a DIFFERENT facility's portal string,
  // which is worse than the bare fallback below.
  const aligned = ids.length === collectedEntries.length;

  const names = ids.map((id, index) => {
    const named = facilities.find((facility) => facility.id === id)?.name;
    if (named) return named;
    const fromCollection =
      aligned && collectedEntries[index] ? collectedFacilityLabel(collectedEntries[index], []) : '';
    return fromCollection && fromCollection !== 'facility' ? fromCollection : `Facility ${String(id)}`;
  });

  return (
    <div data-testid='facility-section-mapped'>
      <FieldRow label={label} present={present}>
        <ReadOnlyValue value={names.join(', ')} />
      </FieldRow>
    </div>
  );
}

const StagedRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
